import React, { useState } from 'react';
import { History } from 'lucide-react';
import api from '../utils/api';
import AppModal, { modalButton } from './AppModal';

const FIELD_LABELS = {
  status: 'Status',
  customer_name: 'Customer name',
  customer_phone: 'Customer phone',
  customer_email: 'Customer email',
  customer_address: 'Customer address',
  customer_gst: 'Customer GST',
  salesperson_name: 'Salesperson',
  subtotal: 'Subtotal',
  total_gst: 'Total GST',
  grand_total: 'Grand total',
  amountReceived: 'Amount received',
  payment_status: 'Payment status',
  materialDescription: 'Material description',
  termsAndConditions: 'Terms & conditions',
  cancellation_status: 'Cancellation status',
  items: 'Items',
  payments: 'Payments',
};

/**
 * Row action: opens the internal change history of an estimate, invoice or
 * closed order. Shown in the app UI only; it is never part of PDFs or shared links.
 */
const DocumentHistoryButton = ({ docId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  const load = async () => {
    setOpen(true);
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/sales/documents/${docId}/history`);
      setHistory(res.data.history || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  if (!docId) return null;

  return (
    <>
      <button
        onClick={load}
        className="p-2 bg-[#C5A57D]/10 hover:bg-[#C5A57D]/20 rounded-lg transition-colors"
        title="History"
      >
        <History className="h-4 w-4 text-[#0A5F38]" />
      </button>
      {open && (
        <AppModal
          open
          onClose={() => setOpen(false)}
          title="Record History"
          subtitle="Internal only - not included in PDFs or shared links"
          size="lg"
          footer={<button className={modalButton.secondary} onClick={() => setOpen(false)}>Close</button>}
        >
          {loading && <p className="text-sm text-gray-500">Loading...</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!loading && !error && history.length === 0 && (
            <p className="text-sm text-gray-500">No history recorded for this record yet.</p>
          )}
          {history.length > 0 && (
            <div className="bg-white border border-[#E6DFC6] rounded-xl overflow-auto max-h-[60vh] text-left">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-[#0A5F38] text-white sticky top-0">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">Date &amp; Time</th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">User</th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">Action</th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">Changes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map((h, i) => (
                    <tr key={i} className="align-top hover:bg-[#F8F5E6]/60">
                      <td className="px-3 py-2 text-left whitespace-nowrap text-slate-700">{new Date(h.at).toLocaleString()}</td>
                      <td className="px-3 py-2 text-left whitespace-nowrap text-slate-800">
                        {h.byName || 'Unknown'}
                        {h.byRole && <span className="block text-xs text-slate-500 capitalize">{h.byRole}</span>}
                      </td>
                      <td className="px-3 py-2 text-left whitespace-nowrap font-medium text-slate-900">{h.action}</td>
                      <td className="px-3 py-2 text-left text-xs text-slate-700">
                        {(h.changes || []).length === 0 ? '-' : h.changes.map((c, j) => (
                          <div key={j}>
                            <span className="font-medium">{FIELD_LABELS[c.field] || c.field}:</span>{' '}
                            <span className="line-through text-slate-400">{c.from}</span> &rarr; {c.to}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AppModal>
      )}
    </>
  );
};

export default DocumentHistoryButton;
